import { ordinalSuffix } from './invitationLetterUtils';

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const parseConferenceDate = (isoDate) => {
  if (!isoDate) return null;
  const d = new Date(`${isoDate}T12:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d;
};

export const countConferenceDays = (start, end) => {
  const startDate = parseConferenceDate(start);
  const endDate = parseConferenceDate(end || start);
  if (!startDate || !endDate) return 0;
  const diffMs = endDate.getTime() - startDate.getTime();
  return Math.max(1, Math.round(diffMs / (24 * 60 * 60 * 1000)) + 1);
};

export const formatConferenceRangeShort = (start, end) => {
  const startDate = parseConferenceDate(start);
  const endDate = parseConferenceDate(end || start);
  if (!startDate) return '';
  if (!endDate || startDate.getTime() === endDate.getTime()) {
    return `${startDate.getDate()} ${MONTH_NAMES[startDate.getMonth()]} ${startDate.getFullYear()}`;
  }

  const startMonth = MONTH_NAMES[startDate.getMonth()];
  const endMonth = MONTH_NAMES[endDate.getMonth()];
  const year = endDate.getFullYear();

  if (startMonth === endMonth) {
    return `${startDate.getDate()}–${endDate.getDate()} ${startMonth} ${year}`;
  }

  return `${startDate.getDate()} ${startMonth} – ${endDate.getDate()} ${endMonth} ${year}`;
};

export const formatConferenceRangeLong = (start, end) => {
  const startDate = parseConferenceDate(start);
  const endDate = parseConferenceDate(end || start);
  if (!startDate) return '';
  if (!endDate || startDate.getTime() === endDate.getTime()) {
    return `${ordinalSuffix(startDate.getDate())} ${MONTH_NAMES[startDate.getMonth()]}, ${startDate.getFullYear()}`;
  }

  const startMonth = MONTH_NAMES[startDate.getMonth()];
  const endMonth = MONTH_NAMES[endDate.getMonth()];
  const year = endDate.getFullYear();

  if (startMonth === endMonth) {
    return `${ordinalSuffix(startDate.getDate())} to ${ordinalSuffix(endDate.getDate())} ${startMonth}, ${year}`;
  }

  return `${ordinalSuffix(startDate.getDate())} ${startMonth} to ${ordinalSuffix(endDate.getDate())} ${endMonth}, ${year}`;
};

export const formatConferenceRangeWithLocation = (start, end, location) => {
  const range = formatConferenceRangeLong(start, end);
  if (!range) return location || '';
  if (!location) return range;
  return `${range} | ${location}`;
};

export const formatConferenceRangeReceipt = (start, end, location) => {
  const startDate = parseConferenceDate(start);
  const endDate = parseConferenceDate(end || start);
  if (!startDate) return location || '';
  const startMonth = MONTH_NAMES[startDate.getMonth()];
  const endMonth = MONTH_NAMES[endDate.getMonth()];
  const year = endDate?.getFullYear() || startDate.getFullYear();
  let range;
  if (!endDate || startDate.getTime() === endDate.getTime()) {
    range = `${startMonth} ${startDate.getDate()}, ${year}`;
  } else if (startMonth === endMonth) {
    range = `${startMonth} ${startDate.getDate()}-${endDate.getDate()}, ${year}`;
  } else {
    range = `${startMonth} ${startDate.getDate()} - ${endMonth} ${endDate.getDate()}, ${year}`;
  }
  if (!location) return range;
  return `${range} | ${location}`;
};

export const getConferenceDayLabels = (start, end) => {
  const startDate = parseConferenceDate(start);
  const endDate = parseConferenceDate(end || start);
  if (!startDate) return [];

  const labels = [];
  const cursor = new Date(startDate);
  let day = 1;

  while (cursor <= endDate) {
    labels.push({
      day,
      label: `${MONTH_SHORT[cursor.getMonth()]} ${cursor.getDate()}`,
      fullLabel: `${MONTH_NAMES[cursor.getMonth()]} ${cursor.getDate()}, ${cursor.getFullYear()}`,
      date: new Date(cursor),
    });
    cursor.setDate(cursor.getDate() + 1);
    day += 1;
  }

  return labels;
};

export const getConferenceDayDate = (start, dayNumber) => {
  const startDate = parseConferenceDate(start);
  if (!startDate || !dayNumber) return null;
  const date = new Date(startDate);
  date.setDate(date.getDate() + dayNumber - 1);
  return date;
};

/** Last Wednesday, Thursday, and Friday of November for a given year. */
export const getLastNovemberConferenceDates = (year) => {
  const y = Number(year);
  if (!Number.isFinite(y) || y < 2000) return null;

  let lastFriday = null;
  for (let day = 30; day >= 1; day -= 1) {
    const date = new Date(y, 10, day, 12, 0, 0);
    if (date.getDay() === 5) {
      lastFriday = day;
      break;
    }
  }

  if (!lastFriday) return null;

  const pad = (n) => String(n).padStart(2, '0');
  const wednesday = lastFriday - 2;

  return {
    year: y,
    wednesday,
    thursday: lastFriday - 1,
    friday: lastFriday,
    start: `${y}-11-${pad(wednesday)}`,
    end: `${y}-11-${pad(lastFriday)}`,
  };
};

export const inferConferenceYear = (settings = {}) => {
  const sources = [
    settings.hero_subtitle,
    settings.hero_title,
    settings.conference_start,
    settings.conference_end,
  ];

  for (const value of sources) {
    const match = String(value || '').match(/\b(20\d{2})\b/);
    if (match) return Number(match[1]);
  }

  return new Date().getFullYear();
};
